// =============================================================================
// src/features/analytics/analytics-view-state.ts - Recorte ativo do Analytics.
//
// 2026-09-02: escopo (Nucleo · Omar · Contexto), periodo (7/30 dias) e o
// toggle "mostrar reacoes" deixam de ser filtros do DOM e viram o RECORTE que
// o servidor recebe (analytics-scope-where + analytics-client). Persistido por
// visitante em localStorage (conveniencia; try/catch — nunca lanca, e o app
// renderiza certo sem storage). O composition root registra o handler que
// re-hidrata e re-renderiza; sem handler (mock/offline) a troca e 'unavailable'.
// SRP: estado + persistencia + despacho da mudanca. Sem DOM, sem fetch.
// =============================================================================

import {
  ANALYTICS_DEFAULT_FEED_SCOPE,
  parseAnalyticsFeedScope,
  type AnalyticsFeedScope,
} from '../../data/analytics-scope-where';
import {
  ANALYTICS_DEFAULT_FEED_SORT,
  parseAnalyticsFeedSort,
  type AnalyticsFeedSort,
} from '../../data/analytics-feed-sort';
import type { OmarAnalyticsPeriod } from './analytics-types';

export interface AnalyticsViewState {
  readonly scope: AnalyticsFeedScope;
  readonly period: OmarAnalyticsPeriod;
  readonly showReactions: boolean;
  /** 2026-09-20: ordenacao do feed paginado, resolvida NO SERVIDOR. */
  readonly sort: AnalyticsFeedSort;
  /** 1-based. NUNCA volta do storage — ver analyticsViewStateFromJson. */
  readonly page: number;
}

/** Nucleo do cenario AM, 30 dias (o que os numeros sempre mostraram), sem reacoes. */
export const ANALYTICS_DEFAULT_VIEW_STATE: AnalyticsViewState = {
  scope: ANALYTICS_DEFAULT_FEED_SCOPE,
  period: 'last30',
  showReactions: false,
  sort: ANALYTICS_DEFAULT_FEED_SORT,
  page: 1,
};

export type AnalyticsViewChangeOutcome = 'applied' | 'unavailable' | 'failed';

type AnalyticsViewChangeHandler = (viewState: AnalyticsViewState) => Promise<void>;
type AnalyticsViewStorage = Pick<Storage, 'getItem' | 'setItem'>;

const ANALYTICS_VIEW_STORAGE_KEY = 'atalaiaAnalyticsViewState';

let analyticsViewState: AnalyticsViewState | null = null;
let analyticsViewChangeHandler: AnalyticsViewChangeHandler | null = null;
let analyticsViewStorageProvider: () => AnalyticsViewStorage | null = defaultAnalyticsViewStorage;

function defaultAnalyticsViewStorage(): AnalyticsViewStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null; // acessor lanca em preview/thumbnail/browser bloqueando site data
  }
}

/** Periodo do botao/attr → tipado; null para valor desconhecido. */
export function parseAnalyticsPeriod(value: string | null | undefined): OmarAnalyticsPeriod | null {
  return value === 'last7' || value === 'last30' ? value : null;
}

/** Recorte ativo (carrega do storage uma vez; default sem storage/valor invalido). */
export function getAnalyticsViewState(): AnalyticsViewState {
  if (!analyticsViewState) analyticsViewState = readAnalyticsViewState();
  return analyticsViewState;
}

/**
 * Chave de memoizacao da hidratacao do DASHBOARD — 1 fetch por recorte.
 *
 * Deliberadamente SEM sort e page: quem muda so a pagina nao pode disparar
 * re-hidratacao dos 14 roots (serie de 120d, KPIs, autores, frescor) nem
 * remontar a rota. Quem pagina usa analyticsFeedPageKey.
 */
export function analyticsViewStateKey(viewState: AnalyticsViewState): string {
  return `${viewState.scope}|${viewState.period}|${viewState.showReactions ? 'reacoes' : 'sem-reacoes'}`;
}

/** Chave da PAGINA do feed: o recorte do dashboard + ordenacao + pagina. */
export function analyticsFeedPageKey(viewState: AnalyticsViewState): string {
  return `${analyticsViewStateKey(viewState)}|${viewState.sort}|p${viewState.page}`;
}

/**
 * Parcial de troca de recorte com a pagina JA corrigida: qualquer mudanca que
 * troque o CONJUNTO devolve o operador a pagina 1. Ficar na pagina 40 depois de
 * trocar o escopo exibiria um pedaco arbitrario de outro conjunto.
 *
 * Funcao a parte (e nao dentro de updateAnalyticsViewState) porque o ROLLBACK
 * de requestAnalyticsViewChange reescreve o estado inteiro: se a regra morasse
 * no merge, o rollback jogaria o operador para a pagina 1 em vez de devolve-lo
 * a pagina em que estava.
 *
 * @example analyticsViewStateAfterChange({ sort: 'vistas:desc' }) // { sort: 'vistas:desc', page: 1 }
 */
export function analyticsViewStateAfterChange(
  partial: Partial<AnalyticsViewState>,
): Partial<AnalyticsViewState> {
  const trocaConjunto = 'scope' in partial || 'period' in partial
    || 'showReactions' in partial || 'sort' in partial;
  if (!trocaConjunto || 'page' in partial) return partial;
  return { ...partial, page: 1 };
}

/** Atualiza o recorte (parcial) e persiste; devolve o estado novo. */
export function updateAnalyticsViewState(partial: Partial<AnalyticsViewState>): AnalyticsViewState {
  analyticsViewState = { ...getAnalyticsViewState(), ...partial };
  writeAnalyticsViewState(analyticsViewState);
  return analyticsViewState;
}

/** Registrado pelo composition root (initAnalytics) quando o live esta habilitado. */
export function setAnalyticsViewChangeHandler(handler: AnalyticsViewChangeHandler | null): void {
  analyticsViewChangeHandler = handler;
}

/** Troca o recorte e despacha re-hidratacao + re-render; nunca lanca. */
export async function requestAnalyticsViewChange(
  partial: Partial<AnalyticsViewState>,
): Promise<AnalyticsViewChangeOutcome> {
  const previous = getAnalyticsViewState();
  const next = updateAnalyticsViewState(analyticsViewStateAfterChange(partial));
  if (!analyticsViewChangeHandler) return 'unavailable';
  try {
    await analyticsViewChangeHandler(next);
    return 'applied';
  } catch {
    updateAnalyticsViewState(previous); // recorte volta ao que estava na tela
    return 'failed';
  }
}

function readAnalyticsViewState(): AnalyticsViewState {
  try {
    const raw = analyticsViewStorageProvider()?.getItem(ANALYTICS_VIEW_STORAGE_KEY);
    return raw ? analyticsViewStateFromJson(raw) : ANALYTICS_DEFAULT_VIEW_STATE;
  } catch {
    return ANALYTICS_DEFAULT_VIEW_STATE;
  }
}

/** Valida campo a campo: valor estranho no storage cai no default daquele campo. */
function analyticsViewStateFromJson(raw: string): AnalyticsViewState {
  const parsed: unknown = JSON.parse(raw);
  const candidate = typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
  return {
    scope: parseAnalyticsFeedScope(String(candidate['scope'])) ?? ANALYTICS_DEFAULT_VIEW_STATE.scope,
    period: parseAnalyticsPeriod(String(candidate['period'])) ?? ANALYTICS_DEFAULT_VIEW_STATE.period,
    showReactions: candidate['showReactions'] === true,
    sort: parseAnalyticsFeedSort(String(candidate['sort'])) ?? ANALYTICS_DEFAULT_VIEW_STATE.sort,
    // A pagina NAO volta do storage de proposito: reabrir o portal na pagina 47
    // de um recorte de ontem e' uma mentira sobre onde o operador parou.
    page: 1,
  };
}

function writeAnalyticsViewState(viewState: AnalyticsViewState): void {
  try {
    analyticsViewStorageProvider()?.setItem(ANALYTICS_VIEW_STORAGE_KEY, JSON.stringify(viewState));
  } catch {
    // storage cheio/bloqueado: o recorte segue em memoria
  }
}

export function __setAnalyticsViewStorageForTests(provider: () => AnalyticsViewStorage | null): void {
  analyticsViewStorageProvider = provider;
  analyticsViewState = null;
}

export function __resetAnalyticsViewStateForTests(): void {
  analyticsViewState = null;
  analyticsViewChangeHandler = null;
  analyticsViewStorageProvider = defaultAnalyticsViewStorage;
}
